import React from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {useParams} from "react-router-dom";
import {tipAmounts, toTipCurrency} from "../model/constants";
import {
    DeliveryStop,
    DeliveryStopDetails,
    OrdererDeliveryRideInfo,
    rateDelivery,
} from "../model/rideInfo";
import {Feedback} from "./Feedback";
import "../styles/InfoPanel.css";
import "../styles/MapPageView.css";
import {SectionWrapper} from "./SectionWrapper";
import {Stop} from "./Stop";
import {Tips} from "./Tips";
import {DriverInfoCard} from "./DriverInfoCard";
import {CallCourierDialog} from "./CallCourierDialog";

export interface DeliveryStopWithDetails extends DeliveryStop {
    details: DeliveryStopDetails;
}

function getCancelMessage(cancelType: DeliveryStopDetails["cancelType"]): JSX.Element {
    switch (cancelType) {
        case "DRIVER":
            return <FormattedMessage id="order_cancelled_by_courier" />;
        case "PASSENGER":
        case "ORDERER":
        case "RECIPIENT":
        case "SUPPLIER":
        case "LIFTAGO":
            return <FormattedMessage id="order_cancelled" />;
        case undefined:
            return <FormattedMessage id="order_cancelled" />;
    }
}

export function DeliveryInfoPanel({
    ride,
    hasFinished,
}: {
    ride: OrdererDeliveryRideInfo;
    hasFinished: boolean;
}) {
    const intl = useIntl();
    const {shareKey} = useParams<{shareKey: string}>();
    const tipCurrency = toTipCurrency(ride.tipCurrency);
    const rideCancelled =
        ride.stops.filter((stop) => stop.details?.status !== "CANCELLED").length === 0;
    const stopWithDetail = ride.stops.filter(
        (stop): stop is DeliveryStopWithDetails => stop.details != null
    );
    const undeliveredReturn =
        ride.stops.filter(
            (stop) => stop.details?.kind === "RETURN" && stop.details.status === "UNDELIVERED"
        ).length > 0;

    return (
        <div className="info-panel">
            <SectionWrapper>
                {rideCancelled && <h3>{getCancelMessage(ride.stops[0].details?.cancelType)}</h3>}

                {ride.driver && ride.carInfo && (
                    <DriverInfoCard driver={ride.driver} car={ride.carInfo} />
                )}

                {stopWithDetail[0]?.details?.driverPhoneInfo?.phoneNumber && (
                    <CallCourierDialog
                        phoneNumber={stopWithDetail[0].details.driverPhoneInfo.phoneNumber}
                        codeForCommunication={
                            stopWithDetail[0].details.driverPhoneInfo.codeForCommunication
                        }
                        ivrEnabled={stopWithDetail[0].details.driverPhoneInfo.ivrEnabled}
                        maskedNumber={stopWithDetail[0].details.driverPhoneInfo.maskedNumber}
                        guestRide={true}
                    />
                )}

                {ride.stops && (
                    <div className="stops">
                        {stopWithDetail.map((stop) => {
                            const stopName = ["PICKUP", "RETURN"].includes(stop.details.kind)
                                ? stop.details.packageInfo.senderInfo?.name
                                : stop.details.packageInfo.recipientInfo?.name;
                            return (
                                <Stop
                                    key={stop.stopId}
                                    status={stop.details.status}
                                    kind={stop.details.kind}
                                    name={stopName}
                                    verificationCode={stop.details?.verificationCode}
                                    placeName={stop.destinationLocation.placeName}
                                    eta={stop.details?.eta}
                                    pudo={stop.details?.pudo}
                                />
                            );
                        })}
                    </div>
                )}
                {undeliveredReturn && (
                    <p className="subnote">
                        <br />
                        <FormattedMessage
                            id="customer_service_contact_at_grid_page"
                            values={{
                                a: (name) => (
                                    <a
                                        href={intl.formatMessage({id: "contact_page_url"})}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {name}
                                    </a>
                                ),
                            }}
                        />
                    </p>
                )}
                {ride.ordererName ? (
                    <p className="subnote" data-cy="tracking-orderer-info">
                        <br />
                        <FormattedMessage id="delivery_ordered_by" />: {ride.ordererName}
                    </p>
                ) : null}
            </SectionWrapper>
            {hasFinished && (
                <>
                    <SectionWrapper>
                        <Feedback
                            shareKey={shareKey}
                            givenRating={ride.rating}
                            title={intl.formatMessage({id: "how_was_the_delivery"})}
                            indications={ride.ratingIndications}
                            rateFunction={rateDelivery}
                        />
                    </SectionWrapper>
                    <SectionWrapper>
                        <Tips
                            tipAmounts={tipAmounts[tipCurrency]["DELIVERY_ORDERER"]}
                            currency={tipCurrency}
                            shareKey={shareKey}
                            shareKeyType="DELIVERY_ORDERER"
                        />
                    </SectionWrapper>
                </>
            )}
        </div>
    );
}
